// Dot lattice and motion adapted from https://i-futur.com/ for the Push hero.
import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  connect() {
    this.stopBackground = startBackground(this.element)
  }

  disconnect() {
    this.stopBackground?.()
  }
}

function startBackground(hero) {
  const canvas = hero.querySelector('.landing-hero-background');
  if (!canvas) return;

  let context;
  try {
    context = canvas.getContext('2d');
  } catch (error) {
    return; // Keep the CSS dot pattern when canvas is unavailable.
  }
  if (!context) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const cameraDistance = 5;
  let width = 0;
  let height = 0;
  let surfaces = [];
  let color = '';
  let opacity = 0;
  let mobile = false;
  let visible = false;
  let suspended = false;
  let unavailable = false;
  let frame = 0;
  let lastFrame = 0;
  let time = 0;

  // A regular dot lattice on six cube faces. Only camera-facing surfaces render.
  const createSurfaces = (divisions) => {
    const faces = [];
    for (let axis = 0; axis < 3; axis += 1) {
      for (const side of [-1, 1]) {
        const normal = [0, 0, 0];
        normal[axis] = side;
        const points = [];
        for (let row = 0; row < divisions; row += 1) {
          for (let column = 0; column < divisions; column += 1) {
            const point = [0, 0, 0];
            point[axis] = side;
            point[(axis + 1) % 3] = -1 + (row + .5) * 2 / divisions;
            point[(axis + 2) % 3] = -1 + (column + .5) * 2 / divisions;
            points.push(point);
          }
        }
        faces.push({ normal, points });
      }
    }
    return faces;
  };

  const paint = () => {
    if (unavailable || !width || !height) return;
    context.clearRect(0, 0, width, height);
    context.fillStyle = color;
    const yaw = .32 + time * .055;
    const pitch = .12 + Math.sin(time * .11) * .06;
    const cy = Math.cos(yaw);
    const sy = Math.sin(yaw);
    const cx = Math.cos(pitch);
    const sx = Math.sin(pitch);
    const scale = Math.max(width / 2.05, height / 2);

    for (const { normal, points } of surfaces) {
      const normalZ = normal[1] * sx + (normal[2] * cy - normal[0] * sy) * cx;
      if (normalZ * cameraDistance <= 1) continue;

      for (const [x, y, z] of points) {
        const rotatedX = x * cy + z * sy;
        const rotatedZ = z * cy - x * sy;
        const rotatedY = y * cx - rotatedZ * sx;
        const depth = y * sx + rotatedZ * cx;
        const perspective = cameraDistance / (cameraDistance - depth);
        const screenX = width * .5 + rotatedX * scale * perspective;
        const screenY = height * .46 + rotatedY * scale * perspective;
        if (screenX < -4 || screenX > width + 4 || screenY < -4 || screenY > height + 4) continue;

        // Quiet center keeps the headline, description and actions easy to read.
        const distance = Math.hypot((screenX - width * .5) / (width * .44), (screenY - height * .53) / (height * .72));
        const blend = Math.min(1, Math.max(0, (distance - .5) / .5));
        const focus = .035 + .965 * blend * blend * (3 - 2 * blend);
        const radius = (mobile ? 1.35 : 1.8) * perspective;
        context.globalAlpha = opacity * focus * (.65 + .35 * normalZ);
        context.beginPath();
        context.ellipse(screenX, screenY, Math.max(.3, radius * normalZ), radius, 0, 0, Math.PI * 2);
        context.fill();
      }
    }
    context.globalAlpha = 1;
    hero.classList.add('has-hero-background');
  };

  const shouldAnimate = () => visible && !document.hidden && !suspended && !unavailable && !reducedMotion.matches;

  const tick = (now) => {
    frame = 0;
    if (!shouldAnimate()) return;
    if (!lastFrame) lastFrame = now;
    const elapsed = now - lastFrame;
    if (elapsed >= 1000 / (mobile ? 24 : 30)) {
      time += Math.min(elapsed, 100) / 1000;
      lastFrame = now;
      paint();
    }
    frame = window.requestAnimationFrame(tick);
  };

  const syncMotion = () => {
    if (frame) window.cancelAnimationFrame(frame);
    frame = 0;
    lastFrame = 0;
    hero.dataset.backgroundMotion = unavailable ? 'unavailable' : reducedMotion.matches ? 'static' : shouldAnimate() ? 'running' : 'paused';
    if (shouldAnimate()) frame = window.requestAnimationFrame(tick);
  };

  const updateColors = () => {
    const style = window.getComputedStyle(hero);
    color = style.getPropertyValue('--hero-dot').trim() || '#d0d0be';
    opacity = Number.parseFloat(style.getPropertyValue('--hero-dot-alpha')) || .42;
    paint();
  };

  const resize = () => {
    const bounds = hero.getBoundingClientRect();
    width = bounds.width;
    height = bounds.height;
    mobile = width < 700;
    // Cap backing-store size on high-density phones and large desktop displays.
    const density = Math.min(window.devicePixelRatio || 1, mobile ? 1.5 : 2, 2560 / Math.max(1, width), 1800 / Math.max(1, height));
    canvas.width = Math.max(1, Math.round(width * density));
    canvas.height = Math.max(1, Math.round(height * density));
    context.setTransform(density, 0, 0, density, 0, 0);
    surfaces = createSurfaces(mobile ? 26 : 38);
    visible = bounds.bottom > 0 && bounds.top < window.innerHeight;
    updateColors();
    syncMotion();
  };

  const resizeObserver = new ResizeObserver(resize);
  const intersectionObserver = new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting;
    syncMotion();
  });
  const motionChanged = () => { paint(); syncMotion(); };
  const pageHide = () => { suspended = true; syncMotion(); };
  const pageShow = () => { suspended = false; resize(); };
  const contextLost = () => {
    unavailable = true;
    syncMotion();
    hero.classList.remove('has-hero-background');
    canvas.hidden = true;
  };

  canvas.hidden = false;
  resize();
  resizeObserver.observe(hero);
  intersectionObserver.observe(hero);
  reducedMotion.addEventListener('change', motionChanged);
  document.addEventListener('visibilitychange', syncMotion);
  window.addEventListener('pagehide', pageHide);
  window.addEventListener('pageshow', pageShow);
  canvas.addEventListener('contextlost', contextLost);

  return () => {
    suspended = true;
    syncMotion();
    resizeObserver.disconnect();
    intersectionObserver.disconnect();
    reducedMotion.removeEventListener('change', motionChanged);
    document.removeEventListener('visibilitychange', syncMotion);
    window.removeEventListener('pagehide', pageHide);
    window.removeEventListener('pageshow', pageShow);
    canvas.removeEventListener('contextlost', contextLost);
    hero.classList.remove('has-hero-background');
  };
}
